import { cloneDeep, omit } from 'lodash-es';
import type { CardAddress } from '../routes/CardCustomizer/CardCustomizer.types';
import type { DraftJson } from '../routes/CardCustomizer/DraftJson.types';
import { CardDieCut } from '../routes/CardCustomizer/components/CardDieCut';
import type { StationeryPrintOrderDetailsFragment } from '../routes/OrderDetails/OrderDetails.graphql.generated';
import { StationeryDraftFormat, StationeryPrintOrderVendorEnum } from '@graphql/generated-base';
import type { ConfigReturn } from '@static/js/env.config';
import { config } from '@static/js/env.config';
import type { FoilLayerData, LayerData, LayerLayoutData } from '../routes/CardCustomizer/components/Layer.types';
import type { CSSProperties } from 'react';
import type { ConstructEcardUrlOptions } from './ecard.types';

export const isValidCardAddress = (address?: CardAddress) => {
  if (!address) {
    return false;
  }

  const defaultFields = ['address2', 'phoneNumber'];
  // state is not required for United Kingdom and Singapore
  const omittedFields = ['GB', 'SG'].includes(address.countryCode) ? [...defaultFields, 'state'] : defaultFields;

  return Object.values(omit(address, omittedFields)).every(Boolean);
};

export const cardDieCutToShapeConfig = (shape: CardDieCut) => {
  if (shape === CardDieCut.rounded) {
    const radius = `${54 / 16}em`;
    return {
      borderRadius: `${radius} ${radius} ${radius} ${radius}`,
      clipPath: `inset(0 0 round ${radius} ${radius} ${radius} ${radius})`,
    };
  }

  if (shape === CardDieCut.arch) {
    const radius = '11.25em';
    return {
      borderRadius: `${radius} ${radius} 0 0`,
      clipPath: `inset(0 0 round ${radius} ${radius} 0 0)`,
    };
  }

  return {
    borderRadius: '0',
    clipPath: 'inset(0 0 round 0 0 0 0)',
  };
};

export function clamp(min: number, max: number, n: number): number {
  return Math.max(min, Math.min(max, n));
}

export function formatBytes(bytes: number) {
  if (bytes < 1000) return `${Math.round(bytes)} Bytes`;

  const kilobytes = bytes / 1000;
  if (kilobytes < 1000) return `${Math.round(kilobytes)} KB`;

  const megabytes = kilobytes / 1000;
  return `${Math.round(megabytes)} MB`;
}

export function adaptDigitalCardJsonToPaper(cardJSON: DraftJson) {
  const newJson = cloneDeep(cardJSON);
  newJson.digitalCustomizations = {};

  return newJson;
}

// Vendors that only ever produce a print (paper) card. joy_digital is
// handled separately below since it's the one vendor value that maps to the
// digital format.
const PRINT_ONLY_VENDORS: ReadonlySet<StationeryPrintOrderVendorEnum> = new Set([
  StationeryPrintOrderVendorEnum.paperlust,
  StationeryPrintOrderVendorEnum.taylor,
  StationeryPrintOrderVendorEnum.taylor_mm,
]);

export function deriveOrderFormat(order: StationeryPrintOrderDetailsFragment): StationeryDraftFormat | null {
  // Prefer the order's own vendor: it's set once at order creation and never
  // changes, unlike cardDraft.format, which lives on the shared draft and can
  // flip after this order was placed (e.g. a couple orders a digital invite,
  // then later switches the same draft's product to paper, or vice versa).
  // Multiple orders can point at the same draft, so trusting the draft's
  // *current* format here made sibling orders display each other's card
  // type. Checked in both directions so a paper order can't render as
  // digital either, when the shared draft has since flipped to digital.
  if (order.vendor === StationeryPrintOrderVendorEnum.joy_digital) {
    return StationeryDraftFormat.digital;
  }
  if (order.vendor && PRINT_ONLY_VENDORS.has(order.vendor)) {
    return StationeryDraftFormat.print;
  }

  // If the order has a cardDraft, use the format from there
  if (order.cardDraft?.format) {
    return order.cardDraft.format;
  }

  // Else, if the order has a designSetName, it's a print concierge order
  if (order.designSetName) {
    return StationeryDraftFormat.print;
  }

  // Sample Kit orders should fall into print orders bucket
  if (order.product?.productType === 'sampleKit') {
    return StationeryDraftFormat.print;
  }

  return null;
}

const ENV_TO_URL_COMPONENTS: Record<ConfigReturn['joyEnvironment'], string> = {
  local: 'http://localhost:3000',
  development: 'https://dev.withjoy.com',
  stage: 'https://staging.withjoy.com',
  production: 'https://withjoy.com',
};

/**
 * Constructs a personalized eCard URL with optional UTM parameters
 *
 * @param orderNumber - The order number for the eCard
 * @param options - Optional parameters for personalization
 * @returns URL like https://withjoy.com/card/I4EVFQ3463?pid=xxx&utm_source=whatsapp&utm_medium=resend&utm_campaign=premiumEcardGuest
 */
export const constructEcardUrl = (orderNumber: string, options?: ConstructEcardUrlOptions): string => {
  const url = new URL(`/card/${orderNumber}`, ENV_TO_URL_COMPONENTS[config.joyEnvironment]);

  if (options?.personId) {
    url.searchParams.set('pid', options.personId);
  }

  if (options?.guestKey) {
    url.searchParams.set('guestKey', options.guestKey);
  }

  if (options?.utmSource) {
    url.searchParams.set('utm_source', options.utmSource);
    url.searchParams.set('utm_medium', 'resend');
    url.searchParams.set('utm_campaign', 'premiumEcardGuest');
  }

  return url.toString();
};

export const findFoilLayer = (layers: LayerData[]): FoilLayerData | undefined => {
  return layers.find(layer => layer.type === 'foil') as FoilLayerData | undefined;
};

export const getFoilColorName = (colorId: string | undefined, offerings: { colorId: string; displayLabel: string }[]) => {
  if (!colorId) {
    return undefined;
  }

  return offerings.find(foil => foil.colorId === colorId)?.displayLabel;
};

export const getDimensions = (draft: DraftJson['card']) => {
  return (
    // `front?.` is load-bearing: a standalone-envelope draft has no `card.front` at all, and this
    // runs before any step mounts. See `getDimensions.spec.ts` for the contract it holds.
    draft.front?.dimensions ??
    draft.back?.dimensions ??
    draft.layout ?? {
      width: 5,
      height: 7,
      cutMargin: 0.125,
      safeZoneMargin: 0.125,
    }
  );
};

type Orientation = 'Landscape' | 'Portrait' | 'Square';

export function getOrientation(width: number, height: number): Orientation {
  if (width < height) return 'Portrait';
  if (width > height) return 'Landscape';
  return 'Square';
}

export function layerLayoutToCss(layout: LayerLayoutData, scale = 1): CSSProperties {
  return {
    position: 'absolute',
    left: scale * layout.x,
    top: scale * layout.y,
    width: scale * layout.width,
    height: scale * layout.height,
    rotate: `${layout.rotation ?? 0}deg`,
    transformOrigin: 'top left',
  };
}
